import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApprovalState } from '@mg/shared';
import type { ApprovalDecisionDto, Campaign } from '@mg/shared';
import { api, ApiRequestError } from '../lib/api';
import { usePortal } from './PortalContext';
import { PortalBrand } from './PortalBrand';
import { approvalStateLabel, approvalStateTone, formatDateTime } from '../lib/display';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Textarea } from '../components/ui/Field';
import { EmptyState, ErrorState, LoadingState } from '../components/ui/States';
import { useDialogs } from '../components/ui/DialogProvider';

const REVIEW_STATES = [ApprovalState.IN_REVIEW, ApprovalState.COMPLIANCE_CHECK];

/** Messaging / description snippet for a campaign under review. */
function campaignSummary(campaign: Campaign): string {
  return campaign.messaging?.trim() || campaign.description?.trim() || 'No messaging yet.';
}

/** The authenticated stakeholder portal: campaigns in review + a decision panel. */
export function PortalApp() {
  const { principal, devBypass } = usePortal();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['portal-review'],
    // Approval lives on the Campaign now; the portal sees campaigns awaiting review.
    queryFn: ({ signal }) => api.listCampaigns(signal),
  });

  const items = useMemo(
    () => (data ?? []).filter((c) => REVIEW_STATES.includes(c.approvalState)),
    [data],
  );
  const selected = items.find((i) => i.id === selectedId) ?? null;

  const decide = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: ApprovalDecisionDto }) =>
      api.decideCampaign(id, dto),
    onSuccess: () => {
      setSelectedId(null);
      void queryClient.invalidateQueries({ queryKey: ['portal-review'] });
    },
  });

  return (
    // Same standalone-route trap as the connect guides: body/#root are overflow:hidden, so a
    // page outside AppShell must own its scroll or anything past the viewport is unreachable.
    <div className="h-full overflow-y-auto bg-page">
      <header className="flex h-16 items-center justify-between border-b border-line bg-card px-6">
        <PortalBrand label="Approvals" />
        <span className="text-sm text-ink-muted">
          {devBypass ? 'Dev preview' : principal?.user.displayName}
        </span>
      </header>

      <main className="mx-auto grid max-w-6xl gap-6 px-6 py-8 lg:grid-cols-[1.4fr_1fr]">
        <section>
          <h1 className="mb-1 text-xl font-semibold text-ink">Awaiting your review</h1>
          <p className="mb-4 text-sm text-ink-muted">
            {items.length} item{items.length === 1 ? '' : 's'} need a decision.
          </p>

          {isLoading ? (
            <Card>
              <LoadingState label="Loading items…" />
            </Card>
          ) : isError ? (
            <Card>
              <ErrorState
                message={
                  error instanceof ApiRequestError ? error.message : 'Could not load items.'
                }
                onRetry={() => void refetch()}
              />
            </Card>
          ) : items.length === 0 ? (
            <Card>
              <EmptyState title="All caught up" description="Nothing is waiting for your review." />
            </Card>
          ) : (
            <div className="space-y-3">
              {items.map((campaign) => (
                <ReviewRow
                  key={campaign.id}
                  campaign={campaign}
                  selected={campaign.id === selectedId}
                  onSelect={() => setSelectedId(campaign.id)}
                />
              ))}
            </div>
          )}
        </section>

        <section className="lg:sticky lg:top-8 lg:self-start">
          {selected ? (
            <DecisionPanel
              campaign={selected}
              pending={decide.isPending}
              onDecide={(dto) => decide.mutate({ id: selected.id, dto })}
              onClose={() => setSelectedId(null)}
            />
          ) : (
            <Card>
              <EmptyState
                title="Select a campaign"
                description="Choose a campaign on the left to review and decide."
              />
            </Card>
          )}
        </section>
      </main>
    </div>
  );
}

function ReviewRow({
  campaign,
  selected,
  onSelect,
}: {
  campaign: Campaign;
  selected: boolean;
  onSelect: () => void;
}) {
  const postCount = campaign.postCount ?? 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`focus-ring w-full rounded-xl border bg-card p-4 text-left shadow-card transition-colors ${
        selected ? 'border-action ring-1 ring-action/20' : 'border-line hover:border-ink-ghost'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-medium text-ink">{campaign.name}</h3>
        <Badge tone={approvalStateTone[campaign.approvalState]}>
          {approvalStateLabel[campaign.approvalState]}
        </Badge>
      </div>
      <p className="mt-1 line-clamp-2 text-sm text-ink-muted">{campaignSummary(campaign)}</p>
      <p className="mt-2 text-xs text-ink-faint">
        v{campaign.version} · {postCount} post{postCount === 1 ? '' : 's'} · updated{' '}
        {formatDateTime(campaign.updatedAt)}
      </p>
    </button>
  );
}

function DecisionPanel({
  campaign,
  pending,
  onDecide,
  onClose,
}: {
  campaign: Campaign;
  pending: boolean;
  onDecide: (dto: ApprovalDecisionDto) => void;
  onClose: () => void;
}) {
  const { alert } = useDialogs();
  const [comment, setComment] = useState('');

  const submit = (decision: ApprovalDecisionDto['decision']) => {
    if (decision !== 'approve' && !comment.trim()) {
      void alert({
        title: 'Comment required',
        message: 'A comment is required when requesting changes or rejecting.',
      });
      return;
    }
    onDecide({ decision, comment: comment.trim() || undefined });
  };

  return (
    <Card>
      <CardHeader
        title={campaign.name}
        description={`Version ${campaign.version}`}
        action={
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        }
      />
      <CardBody className="space-y-4">
        <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-page p-3 text-sm text-ink-strong">
          {campaignSummary(campaign)}
        </div>

        <div>
          <label htmlFor="decision-comment" className="mb-1 block text-sm font-medium text-ink-strong">
            Comment
          </label>
          <Textarea
            id="decision-comment"
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Required when requesting changes or rejecting…"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Button loading={pending} onClick={() => submit('approve')}>
            Approve
          </Button>
          <Button variant="secondary" loading={pending} onClick={() => submit('request-changes')}>
            Request changes
          </Button>
          <Button variant="danger" loading={pending} onClick={() => submit('reject')}>
            Reject
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
